/**
 * Formatters (03-design-system § 8).
 *
 * Every function takes the raw API value, uses locale `de-DE` and the time zone
 * `Europe/Berlin` explicitly — never the device zone — and is pure. Prices always
 * carry a unit; amounts use tabular numerals in the components.
 */

export const TIME_ZONE = 'Europe/Berlin';
export const LOCALE = 'de-DE';

/** Month abbreviations without a trailing dot, as the design writes them. */
const MONTHS_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const MONTHS_LONG = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];
const WEEKDAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const WEEKDAYS_LONG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

export type LocalDay = string; // YYYY-MM-DD
export type MoneyAmount = { netCents: number; grossCents: number; vatRateBp: number };

/* ------------------------------ day helpers ------------------------------- */

/** Day arithmetic is string arithmetic on `YYYY-MM-DD`; `Date` only as a calendar. */
function partsOf(day: LocalDay): { y: number; m: number; d: number } {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return { y, m, d };
}

function utcOf(day: LocalDay): Date {
  const { y, m, d } = partsOf(day);
  return new Date(Date.UTC(y, m - 1, d));
}

export function addDays(day: LocalDay, n: number): LocalDay {
  const date = utcOf(day);
  date.setUTCDate(date.getUTCDate() + n);
  return date.toISOString().slice(0, 10);
}

export function endOfMonth(day: LocalDay): LocalDay {
  const { y, m } = partsOf(day);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

const dayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The local calendar day in Bruchsal, whatever the device thinks. */
export function todayBerlin(now: Date = new Date()): LocalDay {
  return dayFormatter.format(now);
}

/** ISO timestamp → the local day it falls on. */
export function toBerlinDay(iso: string): LocalDay {
  return dayFormatter.format(new Date(iso));
}

const clockFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * Minutes since local midnight in Bruchsal. Every "is it past 20:00" rule in
 * the app goes through this, so a device in another time zone cannot shift a
 * day-ticket window by an hour.
 */
export function minutesOfDayBerlin(now: Date = new Date()): number {
  const [hour, minute] = clockFormatter.format(now).split(':').map(Number);
  return (hour ?? 0) * 60 + (minute ?? 0);
}

/* --------------------------------- money ---------------------------------- */

/** Intl separates the amount from the € with U+00A0; we match it exactly. */
const NBSP = '\u00a0';

const EURO = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'EUR' });

/** `27370` → `273,70 €` (with a non-breaking space before the €). */
export function formatMoney(cents: number): string {
  return EURO.format(cents / 100);
}

/** `2500` → `25 €`; only for "netto" small print and analytics axes. */
export function formatMoneyShort(cents: number): string {
  return cents % 100 === 0
    ? `${new Intl.NumberFormat(LOCALE).format(cents / 100)}${NBSP}\u20ac`
    : formatMoney(cents);
}

export function formatNet(money: MoneyAmount): string {
  return `${formatMoney(money.netCents)} netto`;
}

/** VAT is gross − net, never recomputed (BR-PRICE-02). */
export function formatVatLine(money: MoneyAmount): { label: string; value: string } {
  const percent = money.vatRateBp / 100;
  const rate = Number.isInteger(percent) ? String(percent) : percent.toFixed(2).replace('.', ',');
  return { label: `darin MwSt. ${rate} %`, value: formatMoney(money.grossCents - money.netCents) };
}

export type Period = 'day' | 'month' | 'block' | 'once';

export function formatPeriod(period: Period): string {
  switch (period) {
    case 'month':
      return 'pro Monat';
    case 'day':
      return 'pro Tag';
    case 'block':
      return 'pro Block';
    case 'once':
      return 'einmalig';
  }
}

/* --------------------------------- dates ---------------------------------- */

/** `2026-09-17` → `Do, 17. Sep` (month without a trailing dot). */
export function formatDayShort(day: LocalDay): string {
  const date = utcOf(day);
  return `${WEEKDAYS_SHORT[date.getUTCDay()]}, ${date.getUTCDate()}. ${MONTHS_SHORT[date.getUTCMonth()]}`;
}

/** The two lines of a `DayStrip` cell (03 § 4.8): `Do` over `17`. */
export function formatDayCell(day: LocalDay): { weekday: string; number: string } {
  const date = utcOf(day);
  return { weekday: WEEKDAYS_SHORT[date.getUTCDay()] ?? '', number: String(date.getUTCDate()) };
}

/** `Donnerstag, 17. September`; the year is added only when it is not the current one. */
export function formatDayLong(day: LocalDay, today: LocalDay = todayBerlin()): string {
  const date = utcOf(day);
  const base = `${WEEKDAYS_LONG[date.getUTCDay()]}, ${date.getUTCDate()}. ${MONTHS_LONG[date.getUTCMonth()]}`;
  return day.slice(0, 4) === today.slice(0, 4) ? base : `${base} ${date.getUTCFullYear()}`;
}

/** `1. Okt.` — the next charge on the profile row, where the year is obvious. */
export function formatDayMonth(day: LocalDay): string {
  const date = utcOf(day);
  const month = MONTHS_SHORT[date.getUTCMonth()] ?? '';
  return `${date.getUTCDate()}. ${month}${month.length === 3 ? '.' : ''}`;
}

/** `17. Sep. 2026` — contracts, cancellations, invoices. "Mai" keeps no dot. */
/**
 * `17. Sep. 2026` for a `LocalDay` — or for a timestamp, which becomes its
 * day in Berlin. The admin passed `createdAt` timestamps here in seven places
 * and printed "NaN. undefined. NaN".
 */
export function formatDate(day: LocalDay | string): string {
  const date = utcOf(day.length > 10 ? todayBerlin(new Date(day)) : day);
  const month = MONTHS_SHORT[date.getUTCMonth()] as string;
  const suffix = month === 'Mai' ? '' : '.';
  return `${date.getUTCDate()}. ${month}${suffix} ${date.getUTCFullYear()}`;
}

/**
 * `2026-10-01T00:00:00Z` → `Oktober 2026` — an opening month (08 § 5.2, § 5.4).
 *
 * Takes an instant rather than a local day, because that is what `opensAt` is.
 * The month is the one in Berlin: an opening at midnight on the 1st must not
 * read as the previous month west of it.
 */
export function formatMonth(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(new Date(iso));
  const month = Number(parts.find((part) => part.type === 'month')?.value ?? '1');
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  return `${MONTHS_LONG[month - 1]} ${year}`;
}

/** `17.09.2026` — form values and admin tables. */
export function formatDateNumeric(day: LocalDay): string {
  const { y, m, d } = partsOf(day);
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
}

/**
 * The inverse of `formatDateNumeric`: what a German keyboard user types into a
 * date field — `17.09.1990`, `17.9.1990`, `17,09,1990`, `17091990` — as a
 * `LocalDay`, or `null` when it is not a real calendar day (31.02. included).
 * An ISO day passes through, so a prefilled value from the API stays valid.
 */
export function parseDateNumeric(text: string): LocalDay | null {
  const raw = text.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  const german = /^(\d{1,2})[.,/ -](\d{1,2})[.,/ -](\d{4})$/.exec(raw) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(raw);
  const [y, m, d] = iso
    ? [Number(iso[1]), Number(iso[2]), Number(iso[3])]
    : german
      ? [Number(german[3]), Number(german[2]), Number(german[1])]
      : [NaN, NaN, NaN];
  if (!Number.isInteger(y) || m < 1 || m > 12 || d < 1) return null;
  const check = new Date(Date.UTC(y, m - 1, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function formatRelativeDay(day: LocalDay, today: LocalDay = todayBerlin()): string {
  if (day === today) return 'Heute';
  if (day === addDays(today, 1)) return 'Morgen';
  return formatDayShort(day);
}

const timeFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** `08:52`, or `15:41 Uhr` with `withUnit`. */
export function formatTime(iso: string, withUnit = false): string {
  const value = timeFormatter.format(new Date(iso));
  return withUnit ? `${value} Uhr` : value;
}

const logTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  fractionalSecondDigits: 3,
  hourCycle: 'h23',
});

/** A device log line's stamp (door protocol § 6.1): `09:27:50.123`, Berlin time. */
export function formatLogTime(epochMs: number): string {
  return logTimeFormatter.format(new Date(epochMs)).replace(',', '.');
}

/** `(6, 20)` → `6 bis 20 Uhr` — no leading zeros. */
export function formatHourRange(from: number, to: number): string {
  return `${from} bis ${to} Uhr`;
}

/** BR-ROOM-01 — meeting rooms are booked in 2-hour blocks. */
export function formatBlock(blockStart: number): string {
  return formatHourRange(blockStart, blockStart + 2);
}

/** `10` → `10 bis 12` — the block buttons of the room picker, which carry no unit. */
export function formatBlockShort(blockStart: number): string {
  return `${blockStart} bis ${blockStart + 2}`;
}

/** `400` → `6 Std. 40 Min.`, `20` → `20 Min.` */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} Min.`;
  if (rest === 0) return `${hours} Std.`;
  return `${hours} Std. ${rest} Min.`;
}

/* ------------------------------ domain bits ------------------------------- */

export function formatFree(free: number, total: number): string {
  return `${free} von ${total} frei`;
}

export interface CardLike {
  brand: string;
  last4: string;
  wallet?: 'apple_pay' | 'google_pay' | null;
}

const BRANDS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  american_express: 'Amex',
};

const WALLETS: Record<string, string> = { apple_pay: 'Apple Pay', google_pay: 'Google Pay' };

/** `Visa •••• 4242`, wallets as `Apple Pay (Visa •••• 4242)`. */
export function formatCardLabel(pm: CardLike): string {
  const brand = BRANDS[pm.brand.toLowerCase()] ?? pm.brand;
  const base = `${brand} •••• ${pm.last4}`;
  const wallet = pm.wallet ? WALLETS[pm.wallet] : null;
  return wallet ? `${wallet} (${base})` : base;
}

export function formatMemberSince(day: LocalDay): string {
  const date = utcOf(day);
  return `Mitglied seit ${MONTHS_SHORT[date.getUTCMonth()]}. ${date.getUTCFullYear()}`;
}

/** `0.52` → `52 %`; percentage-point deltas use `formatPoints`. */
export function formatPercent(value: number, fractionDigits = 0): string {
  return `${new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value * 100)} %`;
}

export function formatPoints(delta: number): string {
  const sign = delta > 0 ? '+' : '';
  return `${sign}${new Intl.NumberFormat(LOCALE).format(delta)} Pkt.`;
}

/** Analytics counts use the thousands separator: `4820` → `4.820`. */
export function formatCount(value: number): string {
  return new Intl.NumberFormat(LOCALE).format(value);
}
